import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { http, fmtGBP, fmtDateShort, formatErr } from "@/lib/api";
import { Plus, UploadSimple, Receipt, FilePdf, X } from "@phosphor-icons/react";

export default function Invoices() {
  const qc = useQueryClient();
  const { data: invoices = [] } = useQuery({ queryKey: ["invoices"], queryFn: () => http.get("/invoices").then(r => r.data) });
  const { data: jobs = [] } = useQuery({ queryKey: ["jobs"], queryFn: () => http.get("/jobs").then(r => r.data) });

  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ job_id: "", vendor: "", amount: 0, invoice_date: new Date().toISOString().slice(0, 10), notes: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const reset = () => { setForm({ job_id: "", vendor: "", amount: 0, invoice_date: new Date().toISOString().slice(0, 10), notes: "" }); setFile(null); setErr(""); };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      let document_file_id = null;
      if (file) {
        const fd = new FormData(); fd.append("file", file);
        const r = await http.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        document_file_id = r.data.id;
      }
      await http.post("/invoices", { ...form, amount: Number(form.amount), document_file_id });
      qc.invalidateQueries({ queryKey: ["invoices"] });
      setShowNew(false); reset();
    } catch (ex) { setErr(formatErr(ex?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const viewPdf = async (id) => {
    try {
      const r = await http.get(`/files/${id}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
    } catch (e) { console.error("[invoices] viewPdf failed", e); }
  };

  const jobMap = Object.fromEntries(jobs.map(j => [j.id, j]));

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="overline">Finance</div>
          <h1 className="text-3xl font-heading font-semibold tracking-tight mt-1">Invoices</h1>
        </div>
        <button onClick={() => setShowNew(true)} data-testid="invoice-new-button"
          className="inline-flex items-center gap-2 h-10 px-4 rounded-md text-white text-sm font-medium" style={{ background: "#002FA7" }}>
          <Plus size={14} weight="bold" /> Upload invoice
        </button>
      </div>

      <div className="rmms-card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] font-heading uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <th className="px-4 py-2.5">Vendor</th>
              <th className="px-4 py-2.5">Job</th>
              <th className="px-4 py-2.5">Date</th>
              <th className="px-4 py-2.5 text-right">Amount</th>
              <th className="px-4 py-2.5">Document</th>
              <th className="px-4 py-2.5">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {invoices.length === 0 && <tr><td colSpan={6} className="px-4 py-12 text-center text-sm text-slate-500" data-testid="invoices-empty">No invoices yet.</td></tr>}
            {invoices.map(i => {
              const job = jobMap[i.job_id];
              return (
                <tr key={i.id} data-testid={`invoice-row-${i.id}`}>
                  <td className="px-4 py-3 font-medium">{i.vendor}</td>
                  <td className="px-4 py-3 text-[12px] font-mono-data text-slate-600">{job?.job_number || "—"}</td>
                  <td className="px-4 py-3 font-mono-data">{fmtDateShort(i.invoice_date)}</td>
                  <td className="px-4 py-3 text-right font-mono-data tabular-nums">{fmtGBP(i.amount)}</td>
                  <td className="px-4 py-3">
                    {i.document_file_id ? (
                      <button onClick={() => viewPdf(i.document_file_id)} data-testid={`invoice-view-pdf-${i.id}`}
                        className="text-xs inline-flex items-center gap-1 text-[#002FA7] hover:underline">
                        <FilePdf size={12} weight="bold" /> View PDF
                      </button>
                    ) : <span className="text-xs text-slate-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-xs">{i.status}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showNew && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50" data-testid="invoice-modal">
          <form onSubmit={submit} className="bg-white border border-slate-200 rounded-md w-full max-w-lg p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="overline">New invoice</div>
                <h2 className="text-xl font-heading font-semibold tracking-tight mt-1">Upload invoice</h2>
              </div>
              <button type="button" onClick={() => { setShowNew(false); reset(); }} className="h-8 w-8 rounded-sm hover:bg-slate-100 flex items-center justify-center"><X size={16} /></button>
            </div>

            <div>
              <label className="overline mb-1.5 block">Linked job *</label>
              <select required value={form.job_id} onChange={e => setForm({ ...form, job_id: e.target.value })} data-testid="invoice-job-select" className="rmms-input">
                <option value="">Select job…</option>
                {jobs.map(j => <option key={j.id} value={j.id}>{j.job_number} — {j.title}</option>)}
              </select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="overline mb-1.5 block">Vendor *</label>
                <input required value={form.vendor} onChange={e => setForm({ ...form, vendor: e.target.value })} data-testid="invoice-vendor-input" className="rmms-input" />
              </div>
              <div>
                <label className="overline mb-1.5 block">Invoice date</label>
                <input type="date" value={form.invoice_date} onChange={e => setForm({ ...form, invoice_date: e.target.value })} data-testid="invoice-date-input" className="rmms-input font-mono-data" />
              </div>
            </div>

            <div>
              <label className="overline mb-1.5 block">Amount (£) *</label>
              <input required type="number" step="0.01" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} data-testid="invoice-amount-input" className="rmms-input font-mono-data" />
            </div>

            <div>
              <label className="overline mb-1.5 block">PDF document</label>
              <label className="flex items-center gap-3 p-3 border border-dashed border-slate-300 rounded-sm cursor-pointer hover:bg-slate-50">
                <UploadSimple size={18} className="text-slate-500" />
                <span className="text-sm text-slate-600 truncate" data-testid="invoice-file-name">{file ? file.name : "Click to attach PDF (optional)"}</span>
                <input data-testid="invoice-file-input" type="file" accept="application/pdf,image/*" onChange={e => setFile(e.target.files?.[0] || null)} className="hidden" />
              </label>
            </div>

            <div>
              <label className="overline mb-1.5 block">Notes</label>
              <input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} data-testid="invoice-notes-input" className="rmms-input" />
            </div>

            {err && <div className="text-sm text-rose-600 border-l-2 border-rose-500 pl-3 py-1">{err}</div>}

            <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
              <button type="button" onClick={() => { setShowNew(false); reset(); }} className="h-10 px-4 text-sm border border-slate-200 rounded-md">Cancel</button>
              <button type="submit" disabled={busy} data-testid="invoice-submit-button" className="h-10 px-4 rounded-md text-white text-sm inline-flex items-center gap-1.5 disabled:opacity-60" style={{ background: "#002FA7" }}>
                <Receipt size={14} weight="bold" /> {busy ? "Saving…" : "Save invoice"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
